import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { TablesInsert, TablesUpdate } from '@/integrations/supabase/types';

import type { FixedAsset } from '@/integrations/supabase/fixedasset.models';

export type { FixedAsset };

export function useFixedAssets(filters?: { type?: string; searchQuery?: string }) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['fixed-assets', user?.id, filters],
    queryFn: async () => {
      let query = supabase
        .from('fixed_assets')
        .select('*')
        .order('created_at', { ascending: false });

      if (filters?.type && filters.type !== 'all') {
        query = query.eq('asset_type', filters.type);
      }
      
      if (filters?.searchQuery) {
        const term = `%${filters.searchQuery}%`;
        query = query.or(`name.ilike.${term},location.ilike.${term},asset_type.ilike.${term}`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as FixedAsset[];
    },
    enabled: !!user,
  });
}

export function useFixedAssetsPaginated(
  filters?: { type?: string; searchQuery?: string },
  pageSize: number = 20
) {
  const { user } = useAuth();
  
  return useInfiniteQuery({
    queryKey: ['fixed-assets-paginated', user?.id, filters, pageSize],
    queryFn: async ({ pageParam = 0 }) => {
      let query = supabase
        .from('fixed_assets')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(pageParam, pageParam + pageSize - 1);

      if (filters?.type && filters.type !== 'all') {
        query = query.eq('asset_type', filters.type);
      }
      
      if (filters?.searchQuery) {
        const term = `%${filters.searchQuery}%`;
        query = query.or(`name.ilike.${term},location.ilike.${term},asset_type.ilike.${term}`);
      }

      const { data, error, count } = await query;
      if (error) throw error;
      
      return {
        assets: data as FixedAsset[],
        nextCursor: pageParam + pageSize,
        hasMore: count ? pageParam + pageSize < count : false,
        totalCount: count || 0,
      };
    },
    getNextPageParam: (lastPage) => {
      return lastPage.hasMore ? lastPage.nextCursor : undefined;
    },
    initialPageParam: 0,
    enabled: !!user,
  });
}

export function useCreateFixedAsset() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (asset: Omit<TablesInsert<'fixed_assets'>, 'user_id'>) => {
      const { data, error } = await supabase
        .from('fixed_assets')
        .insert({ ...asset, user_id: user!.id })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fixed-assets'] }),
  });
}

export function useUpdateFixedAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: TablesUpdate<'fixed_assets'> & { id: string }) => {
      const { data, error } = await supabase
        .from('fixed_assets')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fixed-assets'] }),
  });
}

export function useDeleteFixedAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('fixed_assets').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fixed-assets'] }),
  });
}
